// v2 handcart — a barrow-style cart: an open slatted body (vertical stakes
// carrying two horizontal slat rails per side, so daylight shows through), a
// single iron axle with spoked wheels (torus rim, crossed wooden spokes, iron
// hub), two near-horizontal pull handles reaching forward to upturned grips, and
// a pair of prop legs it parks on at the handle end. Pulled toward +z. Ported 1:1
// from age-of-mcp's v2/handcart.ts (the source uses no shared helpers — every
// primitive is inline).
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const BED   = "#8a6f48";   // bed planking
const FRAME = "#6e563a";   // battens, handles, legs
const STAKE = "#5e4426";   // stakes & slats
const SPOKE = "#b89a62";   // pale wheel spokes
const RIM   = "#3a342c";   // iron-shod rim
const IRON  = "#5a5550";   // axle & hubs
const GRIP  = "#4a3520";   // handle grips

const BEDY = 0.165;   // bed floor centre height
const AXZ  = -0.04;   // axle z (slightly aft of centre)
const WY   = 0.108;   // wheel centre height (rim outer radius touches 0)

function handcartParts(): Part[] {
  const parts: Part[] = [];

  // Bed floor + cross battens beneath.
  parts.push({ geometry: new THREE.BoxGeometry(0.30, 0.035, 0.36), color: BED, position: [0, BEDY, -0.02] });
  for (const z of [0.10, -0.14]) {
    parts.push({ geometry: new THREE.BoxGeometry(0.32, 0.02, 0.03), color: FRAME, position: [0, 0.145, z] });
  }

  // Slatted side rails: three stakes + two slats per side.
  for (const sx of [-1, 1]) {
    for (const z of [-0.185, -0.02, 0.145]) {
      parts.push({
        geometry: new THREE.BoxGeometry(0.025, 0.16, 0.025),
        color: STAKE, position: [sx * 0.145, 0.235, z],
      });
    }
    for (const y of [0.225, 0.285]) {
      parts.push({
        geometry: new THREE.BoxGeometry(0.018, 0.03, 0.36),
        color: STAKE, position: [sx * 0.145, y, -0.02],
      });
    }
  }

  // Slatted tailboard (-z) and a low front board (+z).
  for (const y of [0.225, 0.285]) {
    parts.push({ geometry: new THREE.BoxGeometry(0.28, 0.03, 0.018), color: STAKE, position: [0, y, -0.20] });
  }
  parts.push({ geometry: new THREE.BoxGeometry(0.28, 0.045, 0.018), color: STAKE, position: [0, 0.20, 0.16] });

  // Single axle.
  parts.push({
    geometry: new THREE.CylinderGeometry(0.016, 0.016, 0.42, 6),
    color: IRON, position: [0, WY, AXZ], rotation: [0, 0, Math.PI / 2],
  });

  // Spoked wheels (rim torus + crossed spokes + hub). The torus ring is
  // authored in local xy; a 90 degree y-rotation stands it in the world yz
  // plane so the wheel rolls along z on an x axle.
  for (const sx of [-1, 1]) {
    const x = sx * 0.175;
    parts.push({
      geometry: new THREE.TorusGeometry(0.092, 0.016, 4, 8),
      color: RIM, position: [x, WY, AXZ], rotation: [0, Math.PI / 2, 0],
    });
    for (const tilt of [Math.PI / 4, -Math.PI / 4]) {
      parts.push({
        geometry: new THREE.BoxGeometry(0.022, 0.17, 0.022),
        color: SPOKE, position: [x, WY, AXZ], rotation: [tilt, 0, 0],
      });
    }
    parts.push({
      geometry: new THREE.CylinderGeometry(0.026, 0.026, 0.05, 6),
      color: IRON, position: [x, WY, AXZ], rotation: [0, 0, Math.PI / 2],
    });
  }

  // Forward pull handles with upturned grips (+z).
  for (const sx of [-1, 1]) {
    parts.push({
      geometry: new THREE.CylinderGeometry(0.014, 0.014, 0.34, 5),
      color: FRAME, position: [sx * 0.115, 0.185, 0.30],
      rotation: [Math.PI / 2 - 0.12, 0, 0],
    });
    parts.push({
      geometry: new THREE.BoxGeometry(0.018, 0.05, 0.018),
      color: GRIP, position: [sx * 0.115, 0.225, 0.46],
    });
  }

  // Prop legs under the front bed corners.
  for (const sx of [-1, 1]) {
    parts.push({
      geometry: new THREE.BoxGeometry(0.02, 0.155, 0.02),
      color: FRAME, position: [sx * 0.125, 0.0775, 0.135],
    });
  }

  return parts;
}

export const handcartV2 = defineModel({
  id: "handcart-v2",
  batchId: "fable",
  name: "Handcart (v2)",
  tags: ["vehicle","handcart","low-poly","age-of-mcp","fable"],
  summary: "Low-poly handcart.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.4,
  parts: handcartParts,
  animations: [],
});
